import type { DebouncedFunc } from 'lodash';

import debounce from 'lodash/debounce';
import { DependencyList, useEffect, useMemo } from 'react';

import { SEARCH_DEBOUNCE_MS } from '~/common/constants/debounces';
import { Nullable } from '~/types/Nullable';

type SearchableCallback = (args: object) => void;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && isFinite(value);
}

export function useDebounce<C extends (...args: any[]) => any>(
  callback: C,
  wait: number,
  deps: DependencyList,
): DebouncedFunc<C>;
export function useDebounce<C extends (...args: any[]) => any>(
  callback: C,
  wait: number,
): DebouncedFunc<C>;
export function useDebounce<P extends object>(
  callback: (args: P) => void,
  initialParams: Nullable<P>,
): DebouncedFunc<(args: P) => void>;
export function useDebounce<P extends object>(
  callback: (args: P) => void,
): DebouncedFunc<(args: P) => void>;
export function useDebounce(
  callback: SearchableCallback | ((...args: any[]) => any),
  waitOrInitial?: number | object | null,
  maybeDeps?: DependencyList,
): DebouncedFunc<(...args: any[]) => any> {
  let wait: number;
  let debounceDeps: DependencyList;
  let isSearch: boolean;
  let initialForEffect: unknown;

  if (arguments.length === 1) {
    isSearch = true;
    initialForEffect = null;
    wait = SEARCH_DEBOUNCE_MS;
    debounceDeps = [callback];
  } else if (arguments.length === 2) {
    if (isFiniteNumber(waitOrInitial)) {
      isSearch = false;
      initialForEffect = undefined;
      wait = waitOrInitial;
      debounceDeps = [callback];
    } else {
      isSearch = true;
      initialForEffect = waitOrInitial;
      wait = SEARCH_DEBOUNCE_MS;
      debounceDeps = [callback];
    }
  } else {
    isSearch = false;
    initialForEffect = undefined;
    if (!isFiniteNumber(waitOrInitial)) {
      throw new TypeError(
        'useDebounce: the second argument must be a finite number when three arguments are passed',
      );
    }
    wait = waitOrInitial;
    debounceDeps = maybeDeps ?? [callback];
  }

  const debounced = useMemo(
    () => debounce(callback, wait),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `debounceDeps` is the public dependency list for the debounced instance
    [wait, ...debounceDeps],
  );

  useEffect(() => {
    return () => {
      debounced.cancel();
    };
  }, [debounced]);

  useEffect(() => {
    if (!isSearch) {
      return;
    }
    if (initialForEffect) {
      (callback as (a: object) => void)(initialForEffect);
    }
  }, [isSearch, initialForEffect, callback]);

  return debounced;
}
